/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import {
  listTeamAuditLogsPage,
  countTeamAuditLogs,
  type AuditLogRow,
  type TeamAuditLogsRange,
} from '../../shell/audit/auditLogs';
import { groupAuditRowsByDay } from '../../shell/audit/auditLogPresentation';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { useContentLayout } from '../../shared/layout/useContentLayout';
import { usePaginatedRemoteList } from '../../shared/data/usePaginatedRemoteList';
import { CatalogPagination } from '../../shared/ui/CatalogPagination';
import { MobilePagination as StockMobilePagination } from '../../shared/ui/MobilePagination';
import { ticketColors } from '../../shared/theme/ticketColors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { settingsStyles } from '../../settings/screens/settingsScreen.styles';
import { SettingsAuditListScreen } from '../../settings/screens/components/SettingsAuditListScreen';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { useEmployeesStore, withSelfOwner } from '../store/useEmployeesStore';
import { buildAuthUserLabelById, resolveAuthUserDisplayLabel } from '../../auth/domain/authUserDisplayLabels';
import { useTeamHistoryFilters } from './history/useTeamHistoryFilters';
import { AuditCategoryTabs } from './history/AuditCategoryTabs';
import { TeamHistoryFiltersPanel } from './history/TeamHistoryFiltersPanel';
import { TeamHistoryMobileFilters } from './history/TeamHistoryMobileFilters';
import { TeamHistoryTable } from './history/TeamHistoryTable';
import { buildTeamHistoryCsv } from './history/teamHistoryExport';
import { shareOrDownloadCsv } from '../../shared/csv-core/infra/shareCsv';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { mapTeamAccessError } from '../application/mapTeamAccessError';
import { useTeamAccess } from '../application/useTeamAccess';

const TEAM_HISTORY_PAGE_SIZE_STORAGE_KEY = 'vuelto.audit.team_history.pageSize';

type TeamHistoryQuery = {
  organizationId: string;
  fromIso: string;
  toIso: string;
  userId: string | null;
  categoryFilter: string | null;
};

export const TeamHistoryScreen = () => {
  const { entitlements, loading: entLoading } = usePlanEntitlements();
  const organizationId = entitlements?.organization_id?.trim() ?? '';
  const { canHistory, canExportHistory } = useTeamAccess();

  const [selectedRow, setSelectedRow] = useState<AuditLogRow | null>(null);

  const filters = useTeamHistoryFilters('today');
  const periodRange = filters.periodRange;

  const sessionUser = useAuthStore((s) => s.session?.user ?? null);
  const ownerAuthUserId = sessionUser?.id ?? null;
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const labelById = useMemo(
    () =>
      buildAuthUserLabelById({
        sessionUserId: sessionUser?.id,
        sessionUserEmail: sessionUser?.email,
        ownerAuthUserId,
        employees,
      }),
    [sessionUser, ownerAuthUserId, employees],
  );

  const resolveUserLabel = useCallback(
    (userId: string | null) => resolveAuthUserDisplayLabel(userId, labelById, ownerAuthUserId),
    [labelById, ownerAuthUserId],
  );

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const [id, name] of Object.entries(labelById)) {
      seen.set(id, name);
    }
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [labelById]);

  const query = useMemo<TeamHistoryQuery>(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      organizationId,
      fromIso: periodRange?.fromIso ?? `${today}T00:00:00.000Z`,
      toIso: periodRange?.toIso ?? `${today}T23:59:59.999Z`,
      userId: filters.serverFilters.userId,
      categoryFilter: filters.serverFilters.categoryFilter,
    };
  }, [organizationId, periodRange?.fromIso, periodRange?.toIso, filters.serverFilters.userId, filters.serverFilters.categoryFilter]);

  const criteriaKey = useMemo(
    () => `org:${organizationId}|from:${query.fromIso}|to:${query.toIso}|user:${query.userId ?? ''}|cat:${query.categoryFilter ?? ''}`,
    [organizationId, query.fromIso, query.toIso, query.userId, query.categoryFilter],
  );

  const fetchPage = useCallback(
    ({ limit, offset, query: q }: { limit: number; offset: number; query: TeamHistoryQuery }) =>
      listTeamAuditLogsPage(offset, q.organizationId, limit, {
        fromIso: q.fromIso,
        toIso: q.toIso,
        userId: q.userId,
        categoryFilter: q.categoryFilter,
      }),
    [],
  );

  const countFn = useCallback(
    (q: TeamHistoryQuery) =>
      countTeamAuditLogs(q.organizationId, { fromIso: q.fromIso, toIso: q.toIso, userId: q.userId, categoryFilter: q.categoryFilter }),
    [],
  );

  const enabled = !!organizationId && !entLoading && canHistory;

  const paging = usePaginatedRemoteList<AuditLogRow, TeamHistoryQuery>({
    storageKey: TEAM_HISTORY_PAGE_SIZE_STORAGE_KEY,
    defaultPageSize: 25,
    criteriaKey,
    query,
    fetchPage,
    count: countFn,
    enabled,
    logLabel: 'TeamHistory',
  });

  const rows = paging.items;
  const loading = paging.loading;
  const hasMore = paging.hasMore;
  const totalCount = paging.totalCount;
  const pageIndex = paging.pageIndex;
  const pageSize = paging.pageSize;
  const setPageSize = paging.setPageSize;
  const loadNextPage = paging.loadNextPage;
  const loadPreviousPage = paging.loadPreviousPage;
  const reloadFromStart = paging.reloadFromStart;

  const filteredRows = useMemo(() => filters.filteredRows(rows), [filters, rows]);
  const sections = useMemo(() => groupAuditRowsByDay(filteredRows), [filteredRows]);

  const loadFirst = useCallback(
    async (opts?: { force?: boolean }) => {
      if (!organizationId) return { silent: true };
      if (opts?.force) {
        await reloadFromStart();
      }
    },
    [organizationId, reloadFromStart],
  );

  const { refreshing, onRefresh } = useScreenLoadShell('TeamHistory', loadFirst);

  const handleExportCsv = useCallback(async () => {
    if (totalCount === 0) {
      showInfo('Exportar CSV', 'No hay eventos con estos filtros.');
      return;
    }
    try {
      // Fetch all rows for the filtered range (not just the current page)
      const allRows: AuditLogRow[] = [];
      const exportPageSize = 200;
      for (let offset = 0; offset < totalCount; offset += exportPageSize) {
        const batch = await listTeamAuditLogsPage(offset, organizationId, exportPageSize, {
          fromIso: query.fromIso,
          toIso: query.toIso,
          userId: query.userId,
          categoryFilter: query.categoryFilter,
        });
        allRows.push(...batch);
        if (batch.length < exportPageSize) break;
      }
      if (allRows.length === 0) {
        showInfo('Exportar CSV', 'No hay eventos con estos filtros.');
        return;
      }
      const csv = buildTeamHistoryCsv(allRows, resolveUserLabel);
      const filename = `historial_equipo_${new Date().toISOString().slice(0, 10)}.csv`;
      const outcome = await shareOrDownloadCsv(csv, filename);
      if (outcome === 'ok') {
        showSuccess('CSV descargado', `${filename} (${allRows.length} eventos)`);
      }
    } catch (e) {
      console.warn('[TeamHistoryScreen] export csv', e);
      const msg = mapTeamAccessError(
        e instanceof Error ? e.message : undefined,
        'No se pudo generar el archivo.'
      );
      showError('No se pudo exportar', msg);
    }
  }, [totalCount, organizationId, query, resolveUserLabel]);

  const { layoutWebShell } = useContentLayout('settingsWide');

  const headerExtra = layoutWebShell ? (
    <>
      <AuditCategoryTabs value={filters.category} onChange={filters.setCategory} />
      <TeamHistoryFiltersPanel filters={filters} userOptions={userOptions} />
    </>
  ) : (
    <TeamHistoryMobileFilters filters={filters} userOptions={userOptions} />
  );

  const heroActions = canExportHistory ? (
    <TouchableOpacity
      style={styles.exportBtn}
      onPress={() => void handleExportCsv()}
      accessibilityRole="button"
      accessibilityLabel="Exportar historial a CSV"
      testID="perfil.team.history.export-csv"
    >
      <Ionicons name="download-outline" size={16} color={ticketColors.ink} />
      <Text style={styles.exportBtnText}>Exportar CSV</Text>
    </TouchableOpacity>
  ) : null;

  const paginationProps = {
    catalogPageIndex: pageIndex,
    pageSize,
    catalogTotalCount: totalCount,
    hasMore,
    productsLoading: loading,
    productCount: filteredRows.length,
    onPreviousPage: loadPreviousPage,
    onNextPage: loadNextPage,
    listNouns: { singular: 'evento', plural: 'eventos' } as const,
    alwaysShowPagination: true as const,
    pageSizePicker: {
      label: 'Eventos por página',
      options: [10, 25, 50] as const,
      value: (pageSize <= 50 ? pageSize : 50) as 10 | 25 | 50,
      onChange: setPageSize as (size: number) => void,
      accessibilityLabelPrefix: 'Eventos por página',
    },
  };

  const desktopTable = layoutWebShell ? (
    <View style={styles.tableWrap}>
      <TeamHistoryTable
        rows={filteredRows}
        resolveUserLabel={resolveUserLabel}
        onSelectRow={setSelectedRow}
      />
      <CatalogPagination {...paginationProps} />
    </View>
  ) : undefined;

  if (!entLoading && !organizationId) {
    return (
      <View style={{ flex: 1, backgroundColor: ticketColors.bg, padding: spacing.lg }}>
        <View style={[settingsStyles.banner, settingsStyles.bannerAccent]}>
          <Text style={settingsStyles.bannerBody}>
            No encontramos el negocio. Reintentá cuando tengas conexión.
          </Text>
        </View>
      </View>
    );
  }

  if (!entLoading && !canHistory) {
    return (
      <View style={{ flex: 1, backgroundColor: ticketColors.bg, padding: spacing.lg }}>
        <View style={[settingsStyles.banner, settingsStyles.bannerAccent]}>
          <Text style={settingsStyles.bannerBody}>
            No tenés permiso para ver el historial del equipo.
          </Text>
        </View>
      </View>
    );
  }

  return (
    <SettingsAuditListScreen
      lead="Lo que pasó en el negocio: equipo, caja, ventas, catálogo, stock y más."
      errorMessage={null}
      sections={sections}
      loading={loading || entLoading}
      refreshing={refreshing}
      onRefresh={() => void onRefresh()}
      loadingMore={false}
      hasMore={false}
      onLoadMore={() => {}}
      emptyLabel={organizationId ? 'No hay eventos con estos filtros.' : '…'}
      selectedRow={selectedRow}
      onSelectRow={setSelectedRow}
      headerExtra={headerExtra}
      heroActions={heroActions}
      desktopTable={desktopTable}
      resolveUserLabel={resolveUserLabel}
      customFooter={!layoutWebShell ? (
        <StockMobilePagination
          pageIndex={pageIndex}
          pageSize={pageSize}
          totalCount={totalCount ?? 0}
          productCount={filteredRows.length}
          loading={loading}
          hasMore={hasMore}
          onPreviousPage={loadPreviousPage}
          onNextPage={loadNextPage}
        />
      ) : null}
      mobile={!layoutWebShell}
    />
  );
};

const styles = StyleSheet.create({
  exportBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  exportBtnText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '600',
  },
  tableWrap: {
    width: '100%',
    alignItems: 'center',
  },
});
